import { HttpError } from '@forto-dev/ui-platform-http'
import { useQuery } from '@tanstack/react-query'
import { Address, Port } from 'features/Directory'
import { getTrackable, ITransportPlan2 } from 'features/TransportPlan'
import { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react'
import { SOCKET_NAME_TASKS } from 'service/SocketService'
import { useRootStore } from 'service/useRootStore'
import { logError } from 'utils/logger'

import { ShipmentInterface } from '../domain/ShipmentInterface'
import { useShipmentStore } from './useShipmentStore'

export type UI = {
  changed: boolean
  saving: boolean
}

export type ShipmentDetailsContextProps = {
  shipmentId: string
}

export type UseShipmentDetails = {
  shipment: ShipmentInterface
  isCanceled: boolean
  loaded: boolean
  ui: UI
  save: () => void
  updateIncoterm: (incoTerm: string) => void
  onSaved: (shipment: ShipmentInterface) => void
  removeRequiredAction: (actionType: string) => void
  updateBookingParty: (bookingParty: string) => void
  updateShipment: (value: Partial<ShipmentInterface>) => void
}

export const ShipmentDetailsContext = createContext<UseShipmentDetails>({} as UseShipmentDetails)

export const ShipmentDetailsContextProvider: FC<PropsWithChildren<ShipmentDetailsContextProps>> = ({
  children,
  shipmentId,
}) => {
  const { transportPlanService, shipmentEndpoint, uiStore, errorService, authStore, socketManager } = useRootStore()
  const {
    shipment,
    isCanceled,
    updateIncoterm,
    processResponse,
    getRequestBody,
    updateBookingParty,
    removeRequiredAction,
    updateShipmentDetails,
  } = useShipmentStore()
  const [loaded, setLoaded] = useState<boolean>(false)

  const [ui, setUi] = useState<UI>({
    changed: false,
    saving: false,
  })

  useQuery<ShipmentInterface, Error>({
    queryKey: ['shipmentDetails', shipmentId],
    queryFn: async () => {
      const response = await shipmentEndpoint.get(shipmentId)
      onShipmentLoad(response)
      return response
    },
  })

  const getShipmentOrigin = (tp: ITransportPlan2): Address | Port => {
    // @ts-expect-error TS(2322) FIXME: Type 'PortOrAddress | undefined' is not assignable... Remove this comment to see the full error message
    return tp.pre && tp.pre.length ? tp.pre[0].milestones.pickup.address : tp.main[0].route.journey.origin
  }

  const updateShipment = (value: Partial<ShipmentInterface>) => {
    updateShipmentDetails(value)
    setUi({ changed: true, saving: false })
  }

  const getShipmentDestination = (tp: ITransportPlan2): Address | Port => {
    // @ts-expect-error TS(2322) FIXME: Type 'PortOrAddress | undefined' is not assignable... Remove this comment to see the full error message
    return tp.on && tp.on.length ? tp.on[0].milestones.delivery.address : tp.main[0].route.journey.destination
  }

  const assignContainerNumbers = () => {
    shipment.containers.forEach((container) => {
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      const trackableEntry = getTrackable(shipment.tp.main, container._id)

      if (trackableEntry === undefined) {
        const errorMessage = `Carriage plan or trackable entry missing for shipment: ${shipment._id} and container id: ${container._id}. Potential data discrepancy.`
        logError(errorMessage)
      }

      container.containerNumber = trackableEntry?.correlation?.containerNumber ?? ''
    })
  }

  const onLoad = (tp: ITransportPlan2, shipmentResponse: ShipmentInterface) => {
    loadTokens()

    if (shipmentResponse?.tags) {
      // assign client-side _id value to allow editing tags in TagsTable.tsx
      // eslint-disable-next-line no-param-reassign
      shipmentResponse.tags = shipmentResponse.tags.map((t) => {
        return { _id: Math.random(), key: t.key, value: t.value }
      })
    }

    if (tp) {
      updateShipmentDetails(
        processResponse({
          ...shipmentResponse,
          tp,
          origin: getShipmentOrigin(tp),
          destination: getShipmentDestination(tp),
        })
      )
    } else {
      updateShipmentDetails(processResponse({ ...shipmentResponse, tp }))
    }

    setLoaded(true)
    uiStore.loader.hide()
  }

  const loadTokens = () => {
    return authStore.loadShipmentToken(shipmentId)
  }

  const save = async () => {
    setUi({ ...ui, saving: true })
    try {
      const shipmentResponse = await shipmentEndpoint.put(shipment._id, getRequestBody())
      onSaved(shipmentResponse)
    } catch (error) {
      onSaveError(error)
    }
  }

  const onSaved = (shipmentResponse: ShipmentInterface) => {
    updateShipmentDetails({
      ...shipment,
      ...processResponse(shipmentResponse),
    })
    setUi({ changed: false, saving: false })
  }

  const onSaveError = (error) => {
    setUi({ ...ui, saving: true })
    errorService.showDialog(error.message)
  }

  const onServerError = (err: Error) => {
    errorService.process(err)
    uiStore.loader.hide()
    setLoaded(true)
  }

  const onShipmentLoad = async (shipmentResponse: ShipmentInterface) => {
    if (shipmentResponse.mode !== 'seaAir') {
      try {
        const tp = await transportPlanService.get(shipmentId)
        onLoad(tp, shipmentResponse)
      } catch (error) {
        if ((error as HttpError)?.status === 404) {
          // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
          return onLoad(null, shipmentResponse)
        }

        onServerError(error as Error)
      }
    } else {
      // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
      onLoad(null, shipmentResponse)
    }
  }

  useEffect(() => {
    if (shipment && !loaded) {
      assignContainerNumbers()
    }

    return () => {
      const socket = socketManager.get(SOCKET_NAME_TASKS)
      socket && socket.disconnect()
    }
  }, [shipment])

  const state: UseShipmentDetails = useMemo(() => {
    return {
      isCanceled,
      shipment,
      loaded,
      ui,
      save,
      onSaved,
      updateShipment,
      updateIncoterm,
      updateBookingParty,
      removeRequiredAction,
    }
  }, [
    shipment,
    loaded,
    ui,
    isCanceled,
    save,
    onSaved,
    updateShipment,
    updateIncoterm,
    updateBookingParty,
    removeRequiredAction,
  ])

  return <ShipmentDetailsContext.Provider value={state}>{children}</ShipmentDetailsContext.Provider>
}

export const useShipmentDetails = (): UseShipmentDetails => {
  const context = useContext(ShipmentDetailsContext)

  if (!context) {
    throw new Error('Must use useShipmentDetails in ShipmentDetailsContext')
  }

  return context
}
